/**
 * Guard in-app navigation away from the Report Builder (MDRS-555 / audit RB-9).
 *
 * `beforeunload` — wired in the provider — only fires for browser-level exits:
 * closing the tab, reloading, following an external link. It is never fired for
 * a client-side route change, which is how a doctor actually leaves this page.
 * Without this hook the most common exit of all is also the only one with no
 * protection.
 *
 * Mounted by the Report Builder page rather than the provider, for two reasons:
 * the provider lives in <Layout/> and is mounted for the whole app (so it would
 * be guarding routes that have no report open), and `useBlocker` needs router
 * context, which would make the provider unmountable in the unit tests that
 * render it standalone.
 *
 * `shouldBlockFn` is async and runs BEFORE the router commits, so the editor is
 * still mounted and its ProseMirror view still alive — the flush can serialise
 * the .docx normally. That ordering is the whole point: an unmount-time flush
 * races the editor's teardown, and the vendor's `save()` builds its buffer and
 * then dispatches to the destroyed view, whose catch-all returns `null` and
 * throws the document away.
 */
import { useCallback } from "react";

import { useBlocker } from "@tanstack/react-router";

import { useReportBuilderEditor } from "./context";
import { resolveLeaveNavigation } from "./leave-guard";

export const useLeaveNavigationGuard = () => {
  const { hasUnsavedWork, captureForNavigation, markEdited } =
    useReportBuilderEditor();

  // `withResolver` so the decision is rendered as a real dialog rather than a
  // native `window.confirm` — matching DraftConflictModal, the other place this
  // app asks a doctor to choose about unsaved work.
  // Memoised: `useBlocker` lists `shouldBlockFn` in its effect deps, so a fresh
  // arrow each render tears down and re-installs `history.block` on a page that
  // re-renders on every keystroke. Not a correctness bug — an in-flight blocked
  // navigation holds its own blocker reference and isn't stranded by the swap —
  // but it is pure churn on the hot path.
  const shouldBlockFn = useCallback(
    () =>
      resolveLeaveNavigation({
        hasUnsavedWork,
        // Capture only — the upload finishes after the navigation. Awaiting the
        // whole save here held the route change for two network round trips,
        // which reads as a dead nav link rather than as "saving".
        capture: captureForNavigation,
      }),
    [hasUnsavedWork, captureForNavigation]
  );

  const blocker = useBlocker({
    shouldBlockFn,
    // The provider owns the tab-close prompt already; letting the router raise
    // its own as well would show two dialogs for one action.
    enableBeforeUnload: false,
    withResolver: true,
  });

  return {
    blocked: blocker.status === "blocked",
    leave: () => blocker.proceed?.(),
    stay: () => {
      blocker.reset?.();
      // Re-arm the autosave. `captureForNavigation` cancelled the debounce
      // timer on its way in, and the capture then failed — so staying would
      // otherwise leave the work dirty with nothing scheduled, saving nothing
      // until the user happened to type again. The modal must not be the only
      // thing standing between them and the loss it just warned about.
      markEdited();
    },
  };
};
